import { AppRoute } from "app";
import { useAtomValue, useSetAtom } from "jotai";
import { colors } from "theme";

import BottomDrawer from "components/BottomDrawer";
import Button, { ContactButton, ContactButtonMethod } from "components/Button";
import Feedback from "components/Feedback";
import ClientTranslation from "components/ClientTranslation";
import DamagedPartsEstimate from "components/DamagedPartsEstimate";
import Flex from "components/Flex";
import InternalDamageWarning from "components/InternalDamageWarning";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { clientConfigAtom } from "atoms/clientConfig";
import { estimateAtom } from "atoms/estimate";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";
import { usePageTracking } from "hooks/usePageTracking";

import TractableLogo from "../../components/TractableLogo";
import { useConfig } from "../../hooks/useConfig";
import { usePullEstimate } from "pages/SpeedyPayment/hooks/pullEstimate";
import { serverSessionAtom } from "atoms/serverSession";
import { payeeFinalisedAtom } from "atoms/payeeFinalised";

interface CTAProps {
  configSpeedyPaymentEnabled?: boolean;
  sessionSpeedyPaymentEnabled?: boolean;
  estimateSpeedyPaymentEnabled?: boolean;
}

const CTAButton = ({ configSpeedyPaymentEnabled, sessionSpeedyPaymentEnabled, estimateSpeedyPaymentEnabled }: CTAProps) => {
  const { flowCompletionRedirectUrl } = useAtomValue(clientConfigAtom);
  const { t } = useClientTranslation();
  const { onNext, navigateTo } = usePageRouting();

  if (flowCompletionRedirectUrl)
    return (
      <Button href={flowCompletionRedirectUrl} rel="noopener noreferrer" component="a">
        {t("global.continue")}
      </Button>
    );

  if (sessionSpeedyPaymentEnabled) {
    return <Button onClick={onNext}>{t("estimate.speedy_payment.view_options")}</Button>;
  }

  if (configSpeedyPaymentEnabled && !estimateSpeedyPaymentEnabled) {
    return <Button onClick={() => navigateTo(AppRoute.TalkToClaimHandler)}>{t("global.continue")}</Button>;
  }

  return null;
};

export const EstimateRepariable = () => {
  usePageTracking({ name: "Estimate - view" });
  const serverSession = useAtomValue(serverSessionAtom);
  const hasPayeeEnabledInSession = !!serverSession?.enablePayeeJourney;
  const payeeFinalized = useAtomValue(payeeFinalisedAtom)
  const togglePageDrawer = useSetAtom(pageDrawerAtom);
  const estimate = useAtomValue(estimateAtom);
  usePullEstimate(hasPayeeEnabledInSession)
  const { currentProgressStep } = usePageRouting();
  const { displayName: client, estimate: estimateConfig, contactButton, enablePayeeJourney = false } = useConfig();
  const { t } = useClientTranslation();
  const clientHasSpeedyPaymentEnabled = estimateConfig?.repairable?.withSpeedyPayment;
  const showHowIsItCalculatedHelper = estimateConfig?.repairable?.showHowIsItCalculatedHelper ?? true;
  const showSpeedyPayment = clientHasSpeedyPaymentEnabled && estimate?.isSpeedyPaymentEnabled;
  const showInternalDamageWarning =
    estimateConfig?.repairable?.showInternalDamageWarning && estimate?.hasInternalDamage;
  const showFeedback = estimateConfig?.repairable?.showFeedback ?? false;
  const enableSplitFlow = hasPayeeEnabledInSession && enablePayeeJourney && !payeeFinalized;
  const partLevelCostsEnabled = estimateConfig?.repairable?.showDamagedPartList && estimateConfig?.repairable?.showDamagedPartListCosts;
  const partLevelCostsHasOtherCosts = true; // TODO: Check if there are other costs in the estimate, only shows up with `Other costs*` label
  
  return (
    <PageLayout
      title={showSpeedyPayment ? t("estimate.speedy_payment.header") : t("estimate.header")}
      dataTestId="estimateRepairable"
      currentStep={currentProgressStep}
      bottomButtons={
        <CTAButton
          configSpeedyPaymentEnabled={estimateConfig?.repairable?.withSpeedyPayment || enablePayeeJourney}
          sessionSpeedyPaymentEnabled={showSpeedyPayment || enableSplitFlow}
          estimateSpeedyPaymentEnabled={estimate?.isSpeedyPaymentEnabled}
        />
      }
    >
      <Feedback visible={showFeedback}>
      <Flex mx={24} mb={32} mt={8} gap={16}>
        <Flex gap={8}>
          <Text sizeVariant="md">
            <ClientTranslation i18nKey="estimate.description">{t("estimate.description")}</ClientTranslation>
          </Text>
          {showHowIsItCalculatedHelper && (
            <Button onClick={togglePageDrawer} typeVariant="link" style={{ alignSelf: "flex-start" }}>
              {t("estimate.calculate_info.header")}
            </Button>
          )}
        </Flex>

        {contactButton && (
          <ContactButton 
            contactMethod={contactButton.type as ContactButtonMethod}
            url={contactButton.url}
            dataTestId="callToAction">
            <ClientTranslation i18nKey="estimate.contact_button">{{ client }}</ClientTranslation>
          </ContactButton>
        )}
        {estimate && <DamagedPartsEstimate estimate={estimate} />}

        {showInternalDamageWarning && <InternalDamageWarning />}

        { showSpeedyPayment && <Text dataTestId="disclaimer" style={{ color: colors.grey[50] }}>{t("estimate.speedy_payment.disclaimer")}</Text> }

        { /* Note that for the disclaimer below, the * is part of the disclaimer, and should be associated with the other costs as well, e.g. the other costs label should say "Other costs*" */}
        { partLevelCostsEnabled && partLevelCostsHasOtherCosts && <Text dataTestId="disclaimer" style={{ color: colors.grey[50] }}>{t("estimate.part_level_costs.other_costs.disclaimer")}</Text> }
        
        <Flex direction="row" justify="center" mt={24} align="center" gap={8}>
          <TractableLogo />
          <Text sizeVariant="md" style={{ color: colors.grey[90] }}>
            {t("global.powered_by_tractable")}
          </Text>
        </Flex>
      </Flex>
      <BottomDrawer heading={t("estimate.calculate_info.header")}>
        <Text sizeVariant="md">{t("estimate.calculate_info.description")}</Text>
      </BottomDrawer>
      </Feedback>
    </PageLayout>
  );
};
